import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { lifecycleAPI } from '@/lib/api';
import { Sparkles, ArrowRight, Loader2, Info } from 'lucide-react';

const FUND_COLORS = {
  a: '#6B7280', // A — Treasuries
  b: '#2D5F8A', // B — Agg Bond
  c: '#1B2A4A', // C — S&P 500
  d: '#8B6B3B', // D — Completion
  e: '#3B8C6E', // E — EAFE
};

const FUND_LABELS = {
  a: 'A Fund',
  b: 'B Fund',
  c: 'C Fund',
  d: 'D Fund',
  e: 'E Fund',
};

const calcAge = (dob) => {
  if (!dob) return null;
  const d = new Date(dob);
  if (Number.isNaN(d.getTime())) return null;
  const today = new Date();
  let age = today.getFullYear() - d.getFullYear();
  const m = today.getMonth() - d.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < d.getDate())) age--;
  return age;
};

export const RecommendedAllocationWidget = ({ dob, currentAlloc = null }) => {
  const navigate = useNavigate();
  const [suggestion, setSuggestion] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [retirementAge, setRetirementAge] = useState(65);

  const currentAge = calcAge(dob);

  useEffect(() => {
    if (!currentAge) { setLoading(false); return; }
    const load = async () => {
      setLoading(true);
      setErr('');
      try {
        const safeRet = Math.max(retirementAge, currentAge + 1);
        const res = await lifecycleAPI.suggest(currentAge, safeRet);
        setSuggestion(res.data);
      } catch (e) {
        setErr('Unable to load recommendation');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [currentAge, retirementAge]);

  if (loading) {
    return (
      <div className="panel p-5" data-testid="recommended-allocation-widget">
        <span className="overline mb-3 block">Recommended Allocation</span>
        <div className="flex items-center gap-2 text-xs text-[#6B7280]">
          <Loader2 size={14} className="animate-spin" />
          <span>Calculating your target allocation…</span>
        </div>
      </div>
    );
  }

  if (err || !suggestion) {
    return (
      <div className="panel p-5" data-testid="recommended-allocation-widget">
        <span className="overline mb-3 block">Recommended Allocation</span>
        <p className="text-xs text-[#6B7280]">
          {err || 'Add your date of birth to see a personalized glidepath recommendation.'}
        </p>
      </div>
    );
  }

  const fund = suggestion.fund;
  const alloc = fund?.allocation || {};
  const bars = ['a', 'b', 'c', 'd', 'e'].map((k) => ({
    k,
    label: FUND_LABELS[k],
    pct: alloc[k] || 0,
    color: FUND_COLORS[k],
  }));

  const handleApply = () => {
    const params = new URLSearchParams({
      preset: fund.code,
      a: alloc.a ?? 0,
      b: alloc.b ?? 0,
      c: alloc.c ?? 0,
      d: alloc.d ?? 0,
      e: alloc.e ?? 0,
    });
    navigate(`/fund-allocation?${params.toString()}`);
  };

  const diff =
    currentAlloc && Object.keys(currentAlloc).length
      ? Math.round(
          ['a', 'b', 'c', 'd', 'e'].reduce(
            (s, k) => s + Math.abs((alloc[k] || 0) - (currentAlloc[k] || 0)),
            0
          ) / 2
        )
      : null;

  return (
    <div className="panel p-5" data-testid="recommended-allocation-widget">
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-2">
          <Sparkles size={14} className="text-[#3B8C6E]" />
          <span className="overline block">Recommended Allocation</span>
        </div>
        <span
          className="text-[10px] font-mono text-[#9CA3AF]"
          data-testid="recommended-allocation-code"
        >
          {fund.code}
        </span>
      </div>

      <div className="mb-3">
        <div className="text-base font-semibold text-[#1B2A4A]" data-testid="recommended-allocation-name">
          {fund.display_name}
        </div>
        <div className="text-[11px] text-[#6B7280] mt-0.5">{fund.tagline}</div>
      </div>

      {/* Stacked bar */}
      <div
        className="w-full h-3 rounded-sm overflow-hidden flex mb-2"
        data-testid="recommended-allocation-bar"
      >
        {bars.map(
          (b) =>
            b.pct > 0 && (
              <div
                key={b.k}
                style={{ width: `${b.pct}%`, backgroundColor: b.color }}
                title={`${b.label} — ${b.pct}%`}
              />
            )
        )}
      </div>
      <div className="grid grid-cols-5 gap-1 mb-3">
        {bars.map((b) => (
          <div key={b.k} className="text-center" data-testid={`recommended-allocation-cell-${b.k}`}>
            <div className="flex items-center justify-center gap-1">
              <span
                className="inline-block w-1.5 h-1.5 rounded-sm"
                style={{ backgroundColor: b.color }}
              />
              <span className="text-[10px] text-[#6B7280]">{b.label}</span>
            </div>
            <div className="text-xs font-mono font-medium text-[#1B2A4A]">{b.pct}%</div>
          </div>
        ))}
      </div>

      <p
        className="text-[11px] leading-snug text-[#6B7280] bg-[#F7F8F9] border border-[#E2E5E9] rounded px-2.5 py-2 mb-3 flex gap-1.5"
        data-testid="recommended-allocation-rationale"
      >
        <Info size={12} className="text-[#2D5F8A] flex-shrink-0 mt-0.5" />
        <span>{suggestion.rationale}</span>
      </p>

      {diff != null && (
        <p className="text-[11px] text-[#8B6B3B] mb-2" data-testid="recommended-allocation-diff">
          Drift vs your current mix: <span className="font-mono font-medium">{diff} pts</span>
        </p>
      )}

      <div className="flex items-center gap-2">
        <label className="text-[10px] text-[#9CA3AF] flex-shrink-0">Retire at</label>
        <input
          type="number"
          min={currentAge + 1}
          max={75}
          value={retirementAge}
          onChange={(e) => setRetirementAge(parseInt(e.target.value || '65', 10))}
          className="w-14 h-7 px-1.5 text-xs font-mono text-[#1B2A4A] border border-[#D1D5DB] rounded focus:outline-none focus:border-[#1B2A4A]"
          data-testid="recommended-allocation-retirement-age"
        />
        <button
          onClick={handleApply}
          className="ml-auto inline-flex items-center gap-1.5 bg-[#1B2A4A] text-white text-xs font-medium px-3 py-1.5 rounded hover:bg-[#0F1A33] transition-colors"
          data-testid="recommended-allocation-apply-btn"
        >
          Apply to Allocation
          <ArrowRight size={12} />
        </button>
      </div>
    </div>
  );
};

export default RecommendedAllocationWidget;
